import { Component, ComponentInterface, h, Prop, Watch, Host, State, Listen, Element } from '@stencil/core';
import { ActionTrackingEvent, VirtualPageTrackingEvent } from '@union-investment/tracking/src/events';
import { isMagnoliaEditingMode } from '../../helper';
import {
  UiCmsStandardTeaserBody,
  UiCmsStandardTeaserMediaSettings,
} from '../ui-cms-standard-teaser/ui-cms-standard-teaser';
import { UiCmsFullScreenTeaserBody } from '../ui-cms-fullscreen-teaser/ui-cms-fullscreen-teaser';
import { getTeasers } from './fetch-teasers';
import { TeaserAspectRatio } from '../../types/global';

type StandardTeaserStyle = UiCmsStandardTeaserBody['style'];
type FullscreenTeaserStyle = UiCmsFullScreenTeaserBody['style'];

interface Teaser extends UiCmsStandardTeaserBody {
  tags?: string[];
  pageTreeOrdinal?: number;
  pageIdentifier?: string;
  teaserText?: string;
  category?: string;
  textAlignment?: 'center' | 'left';
  imageRatio?: string;
  imageAlternativeText?: string;
  imageCopyright?: string;
  videoSrc?: string;
  videoCopyright?: string;
  videoLoopSetting?: string;
  chipOnImage?: string | null;
  flagOnImage?: string | null;
  chipOnContent1?: string | null;
  chipOnContent2?: string | null;
}
interface UiCmsArticleOverviewBody {
  apiUrl: string;
  apiKey: string;
  parentNodeId: string;
  teaserPerPage: number;
  showTagBar: boolean;
  showPagination: boolean;
  displayedTags?: string[];
  activeTags?: string[];
  filteredTags?: string[];
  isAlignButtons?: boolean;
  columns?: number;
  distribution?: '6_6' | '8_4' | '4_8';
  teaserLayout?: 'STANDARD' | 'ARTICLE';
  tagRelation?: 'AND' | 'OR';
  // The teaser style props/link option props are not part of the api response, therefore they are being passed as props from the backend
  // teaser style props
  style: StandardTeaserStyle;
  linkTitle: string;
  linkStyle: 'text' | 'button-primary' | 'button-secondary' | 'button-cta';
  linkIconOption: 'icon' | 'auto' | 'none';
  linkIcon?: string;
  excludePageIds: string[];
  paginationType: 'pagination' | 'loadMore';
  useTokenForAuthorization: boolean;
  chipOnImageColor?: 'white' | 'primary-200' | 'primary-300' | 'primary-500';
  flagOnImageColor?: 'white' | 'neutral-200' | 'neutral-300' | 'neutral-500';
  chipOnContentColor?: 'white' | 'primary-200' | 'primary-300' | 'primary-500';
  removeSpacing?: boolean;
  metadataPosition?: 'top' | 'bottom';
  linksPosition?: 'left' | 'right';
  mediaSettings?: UiCmsStandardTeaserMediaSettings;
  orientation?: 'horizontal' | 'vertical';
  aspectRatio?: TeaserAspectRatio;
  textAlignment?: 'center' | 'left';
}
interface OidcUser {
  access_token: string;
  id_token: string;
  expires_at: number;
}
@Component({
  tag: 'ui-cms-article-overview',
  styleUrl: 'ui-cms-article-overview.scss',
})
export class UiCmsArticleOverview implements ComponentInterface {
  @Element() el: HTMLUiCmsArticleOverviewElement;

  // Static counter to track the number of instances on the page
  private static instanceCount = 0;

  @Prop({ mutable: true }) body: UiCmsArticleOverviewBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Prop({ mutable: true }) i18n = {
    loadMore: 'Mehr laden',
    noResults: 'Keine Ergebnisse gefunden.',
  };

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  /**
   * The last / most recently fetched batch of teaser objects from the api.
   * When this.body.paginationType === 'pagination', these will be the currently displayed page's teasers.
   * When this.body.paginationType === 'loadMore', these will be the most recently appended teasers at the bottom of the page.
   */
  @State() lastTeasers: Teaser[] = [];

  /**
   * All displayed teaser objects.
   * When this.body.paginationType === 'pagination', these will be the currently displayed page's teasers and equivalent to this.lastTeasers.
   * When this.body.paginationType === 'loadMore', these will be the summation of all fetched teasers.
   */
  @State() allTeasers: Teaser[] = [];

  /**
   * Teaser data that is fetched from the api.
   */
  @State() teaserData: any;

  /**
   * Offset for pagination.
   */
  @State() offset = 0;

  /**
   * Current page.
   */
  @State() page = 1;

  /**
   * Current Tag that was clicked.
   */
  @State() clickedTag: string;

  /**
   * Tags that are clicked.
   */
  @State() clickedTags: string[] = [];

  /**
   * Tags array that is used to filter teasers in the api.
   */
  @State() filteredTags: string[] = [];

  /**
   * Tags to be displayed.
   */
  @State() tags: string[] = [];

  @State() fetchingData = false;

  /**
   * fetchTeaserData endpoint will only use a user token if it's B2B.
   * Based on the 'useTokenForAuthorization' parameter from the body
   * It will determine if it needs to call the fetchBearerToken method.
   */
  oidcUser: OidcUser | null = null;

  /**
   * Check if there are multiple instances of this component on the page.
   * When true, URL updates should be more conservative to avoid conflicts.
   */
  private hasMultipleInstances(): boolean {
    return UiCmsArticleOverview.instanceCount > 1;
  }

  /**
   * Listen for uiLoadMore event triggered by ui-cms-load-more component.
   */
  @Listen('uiLoadMore')
  onLoadMore() {
    this.offset += this.body.teaserPerPage;
    this.page += 1;
    this.updateTeasersAndTags();
  }

  // this handles the offset
  @Listen('fetchResults')
  onPaginationChange(ev) {
    // The following checks if event came from inside this component
    if (!this.el.contains(ev.target)) {
      return;
    }
    // update pagination offset & page
    this.offset = ev.detail.from;
    this.page = this.offset / this.body.teaserPerPage + 1;
    this.updateTeasersAndTags();
  }

  connectedCallback() {
    // Increment the instance counter when component is added to DOM
    UiCmsArticleOverview.instanceCount += 1;
  }

  disconnectedCallback() {
    // Decrement the instance counter when component is removed from DOM
    UiCmsArticleOverview.instanceCount -= 1;
  }

  async componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);

    // fetch user token if it's B2B
    if (this.body.useTokenForAuthorization) this.oidcUser = this.fetchBearerToken();

    // on page refresh, loadMore pagination type should reset to the first batch of fetched teasers and page 1
    if (this.body.paginationType === 'loadMore') this.reset();

    this.initTags();
    if (this.body.apiUrl && this.body.apiKey) this.updateTeaserData();
  }

  private updateTeasersAndTags() {
    // init tags
    this.initTags();
    // update url query params
    this.updateUrlWithQueryParams();
    // fetch new teasers based on offset
    this.updateTeaserData();
  }

  private updateTeaserData = async () => {
    // Only read pagination from URL if there are not multiple instances
    if (!this.hasMultipleInstances() && this.getPageFromUrl()) {
      this.updatePaginationFromUrl();
    }

    // fetch teaser data and save to local state
    this.teaserData = await this.fetchTeaserData();

    if (this.teaserData.teasers) {
      this.lastTeasers = this.addStylePropsToTeasers(this.teaserData.teasers);
      this.allTeasers =
        this.body.paginationType === 'pagination' ? this.lastTeasers : this.allTeasers.concat(this.lastTeasers);
      this.tags = this.body?.displayedTags?.length ? this.body.displayedTags : this.teaserData.tags;
    }
  };

  private addStylePropsToTeasers = (teasers: Teaser[]): Teaser[] => {
    const updatedTeasers = teasers.length
      ? teasers.map((teaser) => {
          const videoMutedByEditorMode = isMagnoliaEditingMode() && !teaser.video?.muted;
          const currentTeaser = { ...teaser };

          if (currentTeaser.image) {
            const imageSrc = typeof currentTeaser.image === 'string' ? currentTeaser.image : '';
            currentTeaser.image = {
              src: imageSrc,
              alt: currentTeaser.imageAlternativeText,
              copyright: currentTeaser.imageCopyright,
            };
          }

          if (currentTeaser.mobileImage) {
            const mobileImageSrc = typeof currentTeaser.mobileImage === 'string' ? currentTeaser.mobileImage : '';
            currentTeaser.mobileImage = {
              src: mobileImageSrc,
              alt: currentTeaser.imageAlternativeText,
              copyright: currentTeaser.imageCopyright,
            };
          }

          if (currentTeaser.videoSrc) {
            const videoLoopSetting = currentTeaser.videoLoopSetting || 'no-loop';
            const { loop, muted } = this.getVideoLoopSettings(videoLoopSetting);

            currentTeaser.video = {
              src: currentTeaser.videoSrc,
              copyright: currentTeaser.videoCopyright,
              muted: videoMutedByEditorMode ? true : muted,
              loop,
            };
          }

          // Process links
          if (currentTeaser.links?.length) {
            currentTeaser.links = currentTeaser.links.map((link) => ({
              ...link,
              title: this.body.linkTitle || link.title,
              ...(this.body?.linkStyle &&
                this.body?.linkStyle !== 'text' && {
                  buttonVariant: this.body.linkStyle.replace('button-', '') as 'primary' | 'secondary' | 'cta',
                }),
              ...(this.body?.linkIconOption &&
                this.body.linkIconOption !== 'none' && { iconOption: this.body.linkIconOption }),
              ...(this.body?.linkIcon &&
                this.body?.linkIconOption !== 'none' && {
                  icon: this.body.linkIcon,
                  iconPath: this.body.linkIcon,
                }),
            }));
          }

          if (currentTeaser.chipOnImage) {
            currentTeaser.imageChip = {
              text: currentTeaser.chipOnImage,
              color: this.body.chipOnImageColor,
            };
          }

          if (currentTeaser.flagOnImage) {
            currentTeaser.flag = {
              text: currentTeaser.flagOnImage,
              color: this.body.flagOnImageColor,
            };
          }

          const chipTexts = [currentTeaser.chipOnContent1, currentTeaser.chipOnContent2].filter(Boolean);
          currentTeaser.contentChips = chipTexts.map((chipText) => ({
            text: chipText,
            color: this.body.chipOnContentColor,
          }));
          return currentTeaser;
        })
      : [];

    return updatedTeasers;
  };

  private getVideoLoopSettings(videoLoopSetting: string): { loop: boolean; muted: boolean } {
    switch (videoLoopSetting) {
      case 'loop-muted':
        return { loop: true, muted: true };
      case 'loop-with-sound':
        return { loop: true, muted: false };
      case 'no-loop':
      default:
        return { loop: false, muted: false };
    }
  }

  private initTags = () => {
    // update active tags
    this.updateActiveTags();
    // update filteredTags array
    this.updateFilteredTags();
  };

  private getTagsFromUrlQueryParams = (): string[] => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const tags = params.get('selectedTags');

    if (tags) {
      return tags.split(',');
    }
    return [];
  };

  private getPageFromUrl = (): number | null => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const page = params.get('page');

    if (page) {
      return Number(page);
    }
    return null;
  };

  private sendTrackingEvents = (tag: string, clickedTags: string[]) => {
    const eventName = `${this.isTagActive(tag) ? 'Active' : 'Inactive'} Tag`;
    const sortedTags = clickedTags.sort().join('.');

    const actionTrackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName,
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: tag,
        virtualTarget: tag,
      },
      category: {
        area: 'Content',
      },
    };

    const virtualPageTrackingEvent: VirtualPageTrackingEvent = {
      eventInfo: {
        eventName: 'Tag Filter Bar',
        eventType: 'VirtualPage',
      },
      attributes: {
        customECommerceParameter: {
          37: sortedTags,
        },
      },
    };

    window.ui?.services?.tracking?.track(actionTrackingEvent);
    window.ui?.services?.tracking?.track(virtualPageTrackingEvent);
  };

  /**
   * Set url query params. Join values with semi-colon.
   * @returns url with query params
   */
  private setUrlQueryParams = (): string => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (this.clickedTags.length === 0) {
      params.delete('selectedTags');
    } else {
      params.set('selectedTags', this.clickedTags.join(','));
    }

    if (this.body?.paginationType === 'pagination' && this.body?.showPagination)
      params.set('page', this.page.toString());

    url.search = params.toString();

    return url.toString();
  };

  private updateUrlWithQueryParams = () => {
    window.history.pushState({}, '', this.setUrlQueryParams());
  };

  private updatePaginationFromUrl = () => {
    // update offset/page if page is set in url query params
    this.page = this.getPageFromUrl();
    this.offset = (this.page - 1) * this.body.teaserPerPage;
  };

  /**
   * Filter teasers by tag. If tag is not clicked, add it to clickedTags and filter teasers by tag.
   * When tag is clicked twice, it counts as unclicked and will be removed from clickedTags.
   */
  private onTagClick = (tag: string) => {
    if (this.clickedTags.includes(tag)) {
      this.clickedTags = this.clickedTags.filter((clickedTag) => clickedTag !== tag);
    } else {
      this.clickedTags = [...this.clickedTags, tag];
    }

    this.clickedTag = tag;
    // send tracking event
    this.sendTrackingEvents(tag, this.clickedTags);
    // reset after clicking on a tag
    this.reset();
    // update url with the selected tags
    this.updateUrlWithQueryParams();
    // update teasers based on clickedTags
    this.updateTeasersAndTags();
  };

  private isTagActive = (tag: string): boolean => this.clickedTags.includes(tag);

  /** Render teaser based on teaserLayout.
   * If teaserLayout is STANDARD, render ui-cms-standard-teaser.
   * If teaserLayout is ARTICLE, render ui-cms-fullscreen-teaser.
   */
  private renderTeaser = (teaser: Teaser, i: number) => {
    return this.body.teaserLayout === 'STANDARD' ? (
      <ui-cms-standard-teaser
        key={i}
        body={{
          headline: teaser.headline,
          text: teaser.teaserText,
          topic: teaser.category,
          date: teaser.date,
          author: teaser.author,
          image: teaser.image,
          mobileImage: teaser.mobileImage,
          imageChip: teaser.imageChip,
          flag: teaser.flag,
          contentChips: teaser.contentChips,
          links: teaser.links,
          linksPosition: this.body.linksPosition || 'left',
          style: this.body.style,
          metadataPosition: this.body.metadataPosition || 'bottom',
          video: teaser.video,
          icon: teaser.icon,
          mediaSettings: this.body.mediaSettings,
          removeSpacing: this.body.removeSpacing,
          orientation: this.body.orientation,
        }}
        align-links={this.body?.isAlignButtons}
      ></ui-cms-standard-teaser>
    ) : (
      <ui-cms-fullscreen-teaser
        key={i}
        body={{
          headline: teaser.headline,
          style: this.body.style as FullscreenTeaserStyle,
          text: teaser.teaserText,
          date: teaser.date,
          topic: teaser.category,
          author: teaser.author,
          textAlignment: this.body.textAlignment,
          image: teaser.image,
          mobileImage: teaser.mobileImage,
          link: teaser.links?.[0],
        }}
        aspect-ratio={this.body.aspectRatio}
      ></ui-cms-fullscreen-teaser>
    );
  };

  private renderTeasers = () => {
    if (this.fetchingData && (this.allTeasers.length === 0 || this.body.paginationType === 'pagination')) {
      return null;
    }

    return this.allTeasers && this.allTeasers.length
      ? this.allTeasers.map((teaser, i) => this.renderTeaser(teaser, i))
      : this.i18n.noResults;
  };

  /**
   * Resets pagination related state values and query params.
   */
  private reset = () => {
    this.page = 1;
    this.offset = 0;
    this.allTeasers = [];
    // Only update URL when there are not multiple instances
    if (!this.hasMultipleInstances()) {
      this.updateUrlWithQueryParams();
    }
  };

  private updateActiveTags = () => {
    // Update clickedTags based on activeTags
    // if url has tags, use them instead of activeTags (for people who share the url)
    if (this.body?.activeTags?.length > 0) {
      // use url tags only on first load and not after clicking on a tag, and only if there are not multiple instances
      if (!this.clickedTag) {
        if (this.hasMultipleInstances()) {
          // When there are multiple instances, always use the component's own activeTags, not URL tags
          this.clickedTags = [...this.body.activeTags];
        } else {
          const urlTags = this.getTagsFromUrlQueryParams();
          this.clickedTags = urlTags.length ? urlTags : [...this.body.activeTags];
        }
      }
      // Only update url with the selected tags when there are not multiple instances
      if (!this.hasMultipleInstances()) {
        this.updateUrlWithQueryParams();
      }
    }

    // if activeTags is not set, get tags from url query params only if there are not multiple instances
    if (!this.body?.activeTags?.length && !this.hasMultipleInstances()) {
      this.clickedTags = this.getTagsFromUrlQueryParams();
    }
  };

  private updateFilteredTags = () => {
    // check if any tags came from this.body.filteredTags and update this.filteredTags accordingly
    if (this.body?.filteredTags?.length) {
      this.filteredTags = [...this.body.filteredTags, ...this.clickedTags];
    } else {
      this.filteredTags = [...this.clickedTags];
    }

    // Remove duplicates from this.filteredTags
    this.filteredTags = Array.from(new Set(this.filteredTags));
  };

  private fetchTeaserData = async () => {
    this.fetchingData = true;
    const teaserData = await getTeasers({
      apiUrl: this.body.apiUrl,
      apiKey: this.body.apiKey,
      teaserParams: {
        tagRelation: this.body.tagRelation,
        limit: this.body.teaserPerPage,
        offset: this.offset,
        parentNodeId: this.body.parentNodeId,
        // add tags property only if tags exist
        // since endpoint doesn't accept empty tags property
        ...(this.filteredTags.length && { tags: this.filteredTags }),
      },
      excludePageIds: this.body?.excludePageIds,
      bearerToken: this.oidcUser?.id_token,
    });
    this.fetchingData = false;
    return {
      teasers: teaserData?.results || [],
      tags: teaserData?.tags || [],
      totalResults: teaserData?.totalResults || 0,
    };
  };

  private fetchBearerToken = (): OidcUser | null => {
    const key = Object.keys(sessionStorage).find((k) => k.startsWith('oidc.user'));
    if (!key) {
      return null;
    }

    const valueAsString = sessionStorage.getItem(key);
    return JSON.parse(valueAsString);
  };

  private renderPagination() {
    if (
      this.body?.showPagination &&
      this.lastTeasers.length &&
      this.teaserData.totalResults > this.body?.teaserPerPage &&
      !this.fetchingData
    ) {
      return this.body.paginationType === 'pagination' ? (
        <ui-search-results-pagination
          results={this.teaserData.totalResults}
          results-per-page={this.body?.teaserPerPage}
          page={this.page}
          scroll-into-element-selector="ui-cms-article-overview"
        ></ui-search-results-pagination>
      ) : (
        <ui-cms-load-more hide={this.allTeasers.length === this.teaserData.totalResults}>
          {this.i18n.loadMore}
        </ui-cms-load-more>
      );
    }

    return null;
  }

  render() {
    return (
      <Host>
        {this.body?.showTagBar && (
          <div class="filter-bar">
            <div class="tag-wrapper">
              {this.tags?.length > 0 &&
                this.tags.map((tag, i) => (
                  <button aria-label={tag}>
                    <sd-tag onClick={() => this.onTagClick(tag)} key={i} selected={this.isTagActive(tag)} toggleable>
                      {tag}
                      {this.isTagActive(tag) && (
                        <sd-icon name="close" library={window.ui.cmsState.iconLibrary}></sd-icon>
                      )}
                    </sd-tag>
                  </button>
                ))}
            </div>
          </div>
        )}
        <div class="teasers">
          {this.body?.teaserLayout === 'STANDARD' ? (
            <ui-cms-standard-teaser-group
              body={{
                columns: Number(this.body?.columns),
                distribution: this.body?.distribution,
                alignLinks: this.body?.isAlignButtons,
              }}
            >
              {this.renderTeasers()}
            </ui-cms-standard-teaser-group>
          ) : (
            <ui-cms-fullscreen-teaser-group
              body={{
                columns: Number(this.body?.columns),
                distribution: this.body?.distribution,
                aspectRatio: this.body.aspectRatio,
              }}
            >
              {this.renderTeasers()}
            </ui-cms-fullscreen-teaser-group>
          )}
        </div>
        {this.renderPagination()}
        {this.fetchingData && <sd-spinner color="primary"></sd-spinner>}
      </Host>
    );
  }
}
